import { z } from 'zod';
/* ------------------------------------------------------------------ */
/* Módulo Proyectos (fase 4): cumplimiento legal, recálculo y reportes */
/* ------------------------------------------------------------------ */
const isoDate = z.iso.date('Fecha inválida');
const days = (from, to) => (Date.parse(to) - Date.parse(from)) / 86_400_000;
/* ---------------------------- cumplimiento ---------------------------- */
/**
 * Nivel de un hallazgo. La distinción es parte de la regla: un tablero donde todo es rojo entrena a ignorar el rojo.
 *   infraction  la norma ya se excedió; es sancionable y hay que decidir qué se hace
 *   warning     es legal, pero está cerca del límite o es riesgoso sostenerlo
 *   info        vale saberlo, sin juicio (incluye la integridad de los datos)
 */
export const COMPLIANCE_SEVERITIES = [
    { code: 'infraction', name: 'Infracción', description: 'La norma ya se excedió' },
    { code: 'warning', name: 'Advertencia', description: 'Legal, pero cerca del límite' },
    { code: 'info', name: 'Informativa', description: 'Sin juicio; incluye datos' },
];
export const complianceSeverity = z.enum(['infraction', 'warning', 'info']);
/**
 * Auditores del sistema fuente portados al modelo por día. «Semana con tipo mezclado» no se porta: era la
 * integridad del paso del modelo semanal al diario y aquí todas las entradas nacen con su desglose.
 */
export const COMPLIANCE_RULES = [
    { code: 'holiday_unpaid', name: 'Feriado sin recargo', article: 'Art. 148' },
    { code: 'rest_day_unpaid', name: 'Descanso sin recargo', article: 'Art. 152' },
    { code: 'overtime_untyped', name: 'Extra sin tipificar', article: 'Art. 139' },
    { code: 'long_day', name: 'Jornada excesiva', article: 'Art. 140' },
    { code: 'weekly_limit', name: 'Tope semanal', article: 'Art. 136' },
    { code: 'weekly_presence', name: 'Presencia semanal alta', article: 'Art. 136' },
    { code: 'consecutive_days', name: 'Días consecutivos', article: 'Art. 152' },
    { code: 'short_rest', name: 'Descanso corto entre turnos', article: null },
    { code: 'overlapping_shifts', name: 'Turnos traslapados', article: null },
    { code: 'unlogged_workday', name: 'Día laborable sin registro', article: null },
];
export const complianceRule = z.enum([
    'holiday_unpaid',
    'rest_day_unpaid',
    'overtime_untyped',
    'long_day',
    'weekly_limit',
    'weekly_presence',
    'consecutive_days',
    'short_rest',
    'overlapping_shifts',
    'unlogged_workday',
]);
export const complianceQuery = z
    .object({
    from: isoDate,
    to: isoDate,
    projectId: z.uuid().optional(),
    workerId: z.uuid().optional(),
    severity: complianceSeverity.optional(),
    rule: complianceRule.optional(),
})
    .refine((v) => v.from <= v.to, { path: ['to'], message: 'El final debe ser posterior al inicio' })
    .refine((v) => days(v.from, v.to) <= 366, { path: ['to'], message: 'Máximo un año' });
/* ---------------------------- recálculo ---------------------------- */
/**
 * Lo que hay que saber de un día antes de recalcularlo. Un día liquidado se muestra pero nunca se aplica.
 */
export const RECOMPUTE_FLAGS = [
    {
        code: 'settled',
        name: 'Liquidado',
        description: 'El día ya se pagó en una liquidación: se muestra, pero no se recalcula.',
    },
    {
        code: 'rate_changed',
        name: 'Tarifa cambiada',
        description: 'La tarifa de la asignación o el monto del viático cambió después de registrar el día.',
    },
    {
        code: 'schedule_changed',
        name: 'Horario cambiado',
        description: 'El horario o el recargo de extra del proyecto cambió: otra frontera ordinaria/extra o tipo de jornada.',
    },
    {
        code: 'surcharge_changed',
        name: 'Recargo cambiado',
        description: 'Cambió el feriado o el día de descanso: el día gana o pierde el pago doble.',
    },
    {
        code: 'amount_inconsistent',
        name: 'Monto inconsistente',
        description: 'El total guardado no se reconstruye con su propio desglose.',
    },
    {
        code: 'below_legal_minimum',
        name: 'Bajo el mínimo legal',
        description: 'Lo guardado paga menos que el mínimo de ley (extra ×1,5; feriado y descanso ×2).',
    },
    {
        code: 'over_12h_blocked',
        name: 'Más de 12 h',
        description: 'El día supera 12 h y el proyecto ya no lo permite: habilitalo en la ficha para recalcularlo.',
    },
];
export const recomputeFlag = z.enum([
    'settled',
    'rate_changed',
    'schedule_changed',
    'surcharge_changed',
    'amount_inconsistent',
    'below_legal_minimum',
    'over_12h_blocked',
]);
export const recomputeQuery = z
    .object({ from: isoDate, to: isoDate })
    .refine((v) => v.from <= v.to, { path: ['to'], message: 'El final debe ser posterior al inicio' })
    .refine((v) => days(v.from, v.to) <= 366, { path: ['to'], message: 'Máximo un año' });
/** Clave de un día de una asignación: `assignmentId:yyyy-MM-dd`. */
export const recomputeDayKey = z.string().regex(/^[0-9a-f-]{36}:\d{4}-\d{2}-\d{2}$/, 'Día inválido');
export const recomputeApplyInput = z.object({
    days: z.array(recomputeDayKey).min(1, 'Elegí al menos un día').max(1000, 'Máximo 1000 días por vez'),
});
/* ---------------------------- reportes ---------------------------- */
export const PROJECT_REPORTS = [
    {
        code: 'financial',
        name: 'Resumen financiero',
        description: 'Cascada completa: terceros, mano de obra, cargas, partidas, utilidad y presupuesto.',
        permission: 'projects:read',
        range: false,
        audience: 'internal',
    },
    {
        code: 'client',
        name: 'Informe para el cliente',
        description: 'Compras por proveedor, horas trabajadas y costos, sin utilidades internas.',
        permission: 'projects:read',
        range: false,
        audience: 'client',
    },
    {
        code: 'client-full',
        name: 'Informe para el cliente (completo)',
        description: 'El informe más los anexos: cada compra y las horas por trabajador.',
        permission: 'projects:read',
        range: false,
        audience: 'client',
    },
    {
        code: 'payroll',
        name: 'Planilla del proyecto',
        description: 'Por trabajador en un rango: horas, recargos, devengado, viáticos, pagos y saldo.',
        permission: 'payroll:read',
        range: true,
        audience: 'internal',
    },
    {
        code: 'worker',
        name: 'Ficha del trabajador',
        description: 'Un trabajador en el proyecto: cada día con sus marcas, horas y montos.',
        permission: 'payroll:read',
        range: true,
        audience: 'internal',
    },
    {
        code: 'closing',
        name: 'Reporte de cierre',
        description: 'Todo el proyecto: compras, planilla, resumen económico, indicadores y cumplimiento.',
        permission: 'payroll:read',
        range: false,
        audience: 'internal',
    },
];
export const projectReportKind = z.enum([
    'financial',
    'client',
    'client-full',
    'payroll',
    'worker',
    'closing',
]);
export const projectReportQuery = z
    .object({
    format: z.enum(['pdf', 'xlsx']).default('pdf'),
    from: isoDate.optional(),
    to: isoDate.optional(),
    /** Ficha del trabajador. */
    workerId: z.uuid().optional(),
})
    .refine((v) => !v.from || !v.to || v.from <= v.to, {
    path: ['to'],
    message: 'El final debe ser posterior al inicio',
})
    .refine((v) => !v.from || !v.to || days(v.from, v.to) <= 731, { path: ['to'], message: 'Máximo dos años' });
//# sourceMappingURL=project-control.js.map